/**
 * Slice 35 — Access tab: Keys & entry (one place to set where keys live).
 */
import { useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { Check, Eye, EyeOff, ImagePlus, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useUpdatePropertyAccessInstructions,
  type PropertyAccessInstructions,
} from "@/hooks/useProperties";
import { usePropertyContext } from "@/contexts/PropertyContext";
import { canEditAccessConfig } from "@/lib/propertyTabs";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { prepareImageFileForUpload } from "@/lib/prepareImageForUpload";
import { useResolvedStorageUrls } from "@/hooks/useResolvedStorageUrls";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ENTRY_METHOD_OPTIONS = [
  { value: "lockbox", label: "Lockbox" },
  { value: "keypad", label: "Keypad / Code" },
  { value: "key", label: "Physical key" },
  { value: "smart_lock", label: "Smart lock" },
  { value: "concierge", label: "Concierge / Front desk" },
  { value: "other", label: "Other" },
] as const;

const MAX_KEY_REFS = 3;

const ENTRY_METHOD_LABELS: Record<string, string> = Object.fromEntries(
  ENTRY_METHOD_OPTIONS.map((o) => [o.value, o.label]),
);

export type AccessTabProperty = PropertyAccessInstructions & {
  key_location_reference_urls?: string[] | null;
};

interface AccessTabProps {
  propertyId: string;
  property: AccessTabProperty;
}

function previewLocationHeadline(location: string): string {
  const trimmed = location.trim();
  if (!trimmed) return "";
  const comma = trimmed.indexOf(",");
  if (comma > 0 && comma < 48) return trimmed.slice(0, comma).trim();
  return trimmed;
}

export default function AccessTab({ propertyId, property }: AccessTabProps) {
  const ctx = usePropertyContext();
  const canEdit = canEditAccessConfig(
    ctx?.role ?? "owner",
    ctx?.accessLevel ?? null,
    ctx?.permissions ?? null,
  );
  const updateInstructions = useUpdatePropertyAccessInstructions();

  const [entryLocation, setEntryLocation] = useState(property.entry_location ?? "");
  const [entryMethod, setEntryMethod] = useState(property.entry_method ?? "");
  const [entryCode, setEntryCode] = useState(property.entry_code ?? "");
  const [showEntryCode, setShowEntryCode] = useState(false);
  const [parkingInstructions, setParkingInstructions] = useState(property.parking_instructions ?? "");
  const [supplyClosetInfo, setSupplyClosetInfo] = useState(property.supply_closet_info ?? "");
  const [accessNotes, setAccessNotes] = useState(property.access_notes ?? "");
  const [keyRefs, setKeyRefs] = useState<string[]>(property.key_location_reference_urls ?? []);
  const displayRefs = useResolvedStorageUrls(keyRefs);
  const [uploadingRefs, setUploadingRefs] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setEntryLocation(property.entry_location ?? "");
    setEntryMethod(property.entry_method ?? "");
    setEntryCode(property.entry_code ?? "");
    setShowEntryCode(false);
    setParkingInstructions(property.parking_instructions ?? "");
    setSupplyClosetInfo(property.supply_closet_info ?? "");
    setAccessNotes(property.access_notes ?? "");
    setKeyRefs(property.key_location_reference_urls ?? []);
  }, [property]);

  useEffect(() => {
    return () => {
      if (savedTimer.current) clearTimeout(savedTimer.current);
    };
  }, []);

  const dirty = useMemo(() => {
    const sameArr =
      keyRefs.length === (property.key_location_reference_urls ?? []).length &&
      keyRefs.every((u, i) => u === (property.key_location_reference_urls ?? [])[i]);
    return (
      entryLocation.trim() !== (property.entry_location ?? "").trim() ||
      (entryMethod || "") !== (property.entry_method ?? "") ||
      entryCode.trim() !== (property.entry_code ?? "").trim() ||
      parkingInstructions.trim() !== (property.parking_instructions ?? "").trim() ||
      supplyClosetInfo.trim() !== (property.supply_closet_info ?? "").trim() ||
      accessNotes.trim() !== (property.access_notes ?? "").trim() ||
      !sameArr
    );
  }, [
    entryLocation,
    entryMethod,
    entryCode,
    parkingInstructions,
    supplyClosetInfo,
    accessNotes,
    keyRefs,
    property,
  ]);

  const previewHeadline = previewLocationHeadline(entryLocation);
  const methodLabel = entryMethod
    ? ENTRY_METHOD_LABELS[entryMethod] ?? entryMethod
    : null;
  const codePreview = entryCode.trim() || null;

  const handleSave = async () => {
    if (!canEdit) return;
    try {
      await updateInstructions.mutateAsync({
        propertyId,
        entry_method: entryMethod || null,
        entry_location: entryLocation.trim() || null,
        // Unit location comes from Hospitable / property address — leave as-is.
        unit_location: property.unit_location ?? null,
        parking_instructions: parkingInstructions.trim() || null,
        supply_closet_info: supplyClosetInfo.trim() || null,
        access_notes: accessNotes.trim() || null,
        key_location_reference_urls: keyRefs,
        entry_code: entryCode.trim() || null,
      });
      setJustSaved(true);
      if (savedTimer.current) clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setJustSaved(false), 2500);
      toast({ title: "Saved", description: "Keys & entry updated." });
    } catch (e) {
      toast({
        title: "Error",
        description: e instanceof Error ? e.message : "Failed to save",
        variant: "destructive",
      });
    }
  };

  const handleRefFiles = async (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const list = Array.from(input.files ?? []);
    input.value = "";
    if (!list.length || !canEdit) return;
    const room = MAX_KEY_REFS - keyRefs.length;
    if (room <= 0) {
      toast({ title: "Limit reached", description: "Up to 3 reference photos." });
      return;
    }
    setUploadingRefs(true);
    try {
      const newUrls: string[] = [];
      for (let i = 0; i < Math.min(list.length, room); i++) {
        const prepared = await prepareImageFileForUpload(list[i]);
        const ext = prepared.name.split(".").pop()?.toLowerCase() || "jpg";
        const safeName = prepared.name.replace(/[^a-zA-Z0-9.-]/g, "_").slice(0, 40);
        const path = `${propertyId}/key_location_refs/property/${Date.now()}_${i}_${safeName}`;
        const { error } = await supabase.storage.from("reference-photos").upload(path, prepared, {
          contentType: prepared.type || `image/${ext === "jpg" ? "jpeg" : ext}`,
          upsert: true,
        });
        if (error) throw error;
        const { data } = supabase.storage.from("reference-photos").getPublicUrl(path);
        newUrls.push(data.publicUrl);
      }
      setKeyRefs((prev) => [...prev, ...newUrls].slice(0, MAX_KEY_REFS));
    } catch (err) {
      toast({
        title: "Upload failed",
        description: err instanceof Error ? err.message : "Try again",
        variant: "destructive",
      });
    } finally {
      setUploadingRefs(false);
    }
  };

  const fieldClass =
    "h-12 w-full rounded-xl border-[1.5px] border-[#dde5ea] bg-white px-4 text-[15px] font-medium text-[#0f1720] outline-none placeholder:text-[#a2b0b9] focus:border-[#24a89a]";

  return (
    <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
      <div className="flex min-w-0 flex-1 flex-col gap-5 rounded-[18px] border-[1.5px] border-[#cfe6e2] bg-white px-5 py-6 sm:px-6">
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-[22px] font-bold tracking-[-0.4px] text-[#0f1720]">Keys &amp; entry</h2>
            {justSaved ? (
              <div className="flex items-center gap-1.5 text-[12.5px] font-bold text-[#15776d]">
                <Check className="h-3.5 w-3.5" strokeWidth={3.2} />
                Saved
              </div>
            ) : null}
          </div>
          <p className="text-[13.5px] font-medium text-[#64748b]">
            Cleaners see this on Start and on the Keys step.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-[12px] font-bold tracking-[0.5px] text-[#15776d]">WHERE TO FIND IT</label>
          <input
            type="text"
            name="access-where-to-find"
            value={entryLocation}
            onChange={(e) => setEntryLocation(e.target.value)}
            disabled={!canEdit}
            placeholder="e.g. Lockbox by front door, left of the porch light"
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            data-1p-ignore="true"
            data-lpignore="true"
            data-form-type="other"
            className={cn(
              "h-16 w-full rounded-[13px] border-[1.5px] bg-[#fbfdfd] px-[18px] text-[18px] font-medium outline-none placeholder:text-[#a2b0b9] focus:border-[#24a89a] focus:bg-white disabled:opacity-60",
              entryLocation.trim() ? "border-[#24a89a] bg-white text-[#0f1720]" : "border-[#c6d2d9] text-[#a2b0b9]",
            )}
          />
        </div>

        <div className="flex flex-col gap-[18px] sm:flex-row">
          <div className="flex flex-1 flex-col gap-2">
            <label className="text-[12px] font-bold tracking-[0.5px] text-[#64748b]">ENTRY METHOD</label>
            <Select
              value={entryMethod || "none"}
              onValueChange={(v) => setEntryMethod(v === "none" ? "" : v)}
              disabled={!canEdit}
            >
              <SelectTrigger className={cn(fieldClass, "h-12")}>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Select</SelectItem>
                {ENTRY_METHOD_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-1 flex-col gap-2">
            <label className="text-[12px] font-bold tracking-[0.5px] text-[#64748b]">
              ENTRY CODE <span className="font-medium text-[#a2b0b9]">optional</span>
            </label>
            <div className="relative">
              <input
                type="text"
                name="access-entry-code"
                value={entryCode}
                onChange={(e) => setEntryCode(e.target.value)}
                disabled={!canEdit}
                placeholder="4821#"
                autoComplete="off"
                autoCapitalize="off"
                spellCheck={false}
                data-1p-ignore="true"
                data-lpignore="true"
                data-form-type="other"
                className={cn(
                  fieldClass,
                  "pr-12 font-mono tracking-[2px]",
                  !showEntryCode && "[-webkit-text-security:disc]",
                )}
              />
              <button
                type="button"
                onClick={() => setShowEntryCode((v) => !v)}
                aria-label={showEntryCode ? "Hide entry code" : "Show entry code"}
                className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg text-[#64748b] hover:bg-[#f4f7f9] hover:text-[#0f1720]"
              >
                {showEntryCode ? (
                  <EyeOff className="h-4 w-4" strokeWidth={2.2} />
                ) : (
                  <Eye className="h-4 w-4" strokeWidth={2.2} />
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2.5">
          <label className="text-[12px] font-bold tracking-[0.5px] text-[#64748b]">
            REFERENCE PHOTOS — WHERE KEYS ARE{" "}
            <span className="font-medium text-[#a2b0b9]">optional, up to 3</span>
          </label>
          <div className="flex flex-wrap gap-2.5">
            {keyRefs.map((url, i) => (
              <div
                key={url}
                className="relative h-[88px] w-[118px] overflow-hidden rounded-xl border border-[#e6edf1] bg-[#eff4f7]"
              >
                <img src={displayRefs[i] || url} alt="" className="h-full w-full object-cover" />
                {canEdit ? (
                  <button
                    type="button"
                    aria-label="Remove reference photo"
                    onClick={() => setKeyRefs((prev) => prev.filter((u) => u !== url))}
                    className="absolute right-1 top-1 rounded-full border border-[#e6edf1] bg-white/95 p-1 shadow-sm"
                  >
                    <X className="h-3.5 w-3.5 text-[#475569]" />
                  </button>
                ) : null}
              </div>
            ))}
            {canEdit && keyRefs.length < MAX_KEY_REFS ? (
              <button
                type="button"
                disabled={uploadingRefs}
                onClick={() => fileInputRef.current?.click()}
                className="flex h-[88px] w-[118px] items-center justify-center rounded-xl border-[1.5px] border-dashed border-[#c6d2d9] bg-[#fbfdfd] text-[13px] font-bold text-[#8494a2]"
              >
                {uploadingRefs ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                  <span className="flex flex-col items-center gap-1">
                    <ImagePlus className="h-4 w-4" />
                    Add photo
                  </span>
                )}
              </button>
            ) : null}
            {keyRefs.length === 0 && canEdit ? (
              <p className="max-w-[420px] self-center text-[13px] font-medium leading-snug text-[#8494a2]">
                A cleaner who has never been here matches the photo instead of reading. One shot of
                the lockbox in context is usually enough.
              </p>
            ) : null}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => void handleRefFiles(e)}
          />
        </div>

        <div className="h-px bg-[#eef3f5]" />

        <div className="grid gap-[18px] sm:grid-cols-2">
          {(
            [
              ["PARKING", parkingInstructions, setParkingInstructions, "Where to park"],
              ["SUPPLY CLOSET", supplyClosetInfo, setSupplyClosetInfo, "Where supplies are"],
            ] as const
          ).map(([label, value, setter, placeholder]) => (
            <div key={label} className="flex flex-col gap-2">
              <label className="text-[12px] font-bold tracking-[0.5px] text-[#8494a2]">{label}</label>
              <input
                type="text"
                value={value}
                onChange={(e) => setter(e.target.value)}
                disabled={!canEdit}
                placeholder={placeholder}
                className="h-11 w-full rounded-xl border-[1.5px] border-[#e6edf1] bg-white px-3.5 text-[14px] font-medium text-[#475569] outline-none placeholder:text-[#c0cad2] focus:border-[#24a89a] disabled:opacity-60"
              />
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-[12px] font-bold tracking-[0.5px] text-[#8494a2]">NOTES</label>
          <textarea
            value={accessNotes}
            onChange={(e) => setAccessNotes(e.target.value)}
            disabled={!canEdit}
            rows={2}
            placeholder="Any other access notes…"
            className="w-full rounded-xl border-[1.5px] border-[#e6edf1] bg-white px-3.5 py-3 text-[14px] font-medium text-[#475569] outline-none placeholder:text-[#c0cad2] focus:border-[#24a89a] disabled:opacity-60"
          />
        </div>

        {canEdit ? (
          <div className="flex flex-wrap items-center gap-3.5">
            <button
              type="button"
              disabled={updateInstructions.isPending || (!dirty && !justSaved)}
              onClick={() => void handleSave()}
              className={cn(
                "flex h-12 items-center rounded-[13px] px-[26px] text-[15.5px] font-bold",
                dirty
                  ? "bg-[#24a89a] text-white hover:bg-[#1f958a]"
                  : "bg-[#e8edf0] text-[#a2b0b9]",
              )}
            >
              {updateInstructions.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save"}
            </button>
            <p className="text-[13px] font-medium text-[#8494a2]">
              Where to find it is the only one cleaners can’t work around.
            </p>
          </div>
        ) : (
          <p className="text-[13px] font-medium text-[#8494a2]">
            View only — ask for Access permission to edit keys &amp; entry.
          </p>
        )}
      </div>

      <aside className="w-full shrink-0 rounded-[18px] border border-[#e6edf1] bg-white p-5 lg:sticky lg:top-0 lg:w-[300px] lg:self-start">
        <div className="mb-3 text-[12px] font-bold tracking-[0.6px] text-[#8494a2]">
          WHAT THE CLEANER WILL SEE
        </div>
        {previewHeadline ? (
          <div className="flex flex-col gap-1.5 rounded-[14px] border-[1.5px] border-[#cfe6e2] bg-white p-4">
            <div className="text-[10.5px] font-bold tracking-[0.8px] text-[#15776d]">WHERE TO FIND IT</div>
            <div className="text-[19px] font-bold leading-snug tracking-[-0.3px] text-[#0f1720]">
              {previewHeadline}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {methodLabel ? (
                <span className="rounded-full bg-[#e4f2f0] px-2.5 py-1 text-[11.5px] font-bold text-[#0d453f]">
                  {methodLabel}
                </span>
              ) : null}
              {codePreview ? (
                <span className="font-mono text-[15px] font-bold tracking-wide text-[#0d453f]">
                  {codePreview}
                </span>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-1 rounded-[14px] border border-dashed border-[#c6d2d9] bg-[#fbfdfd] p-4">
            <div className="text-[10.5px] font-bold tracking-[0.8px] text-[#a2b0b9]">WHERE TO FIND IT</div>
            <div className="text-[16px] font-bold text-[#8494a2]">No key location set</div>
          </div>
        )}
        <p className="mt-3 text-[12.5px] font-medium leading-snug text-[#8494a2]">
          Live preview of the Keys step, so you are writing for a person standing outside rather than
          filling a form.
        </p>
      </aside>
    </div>
  );
}
